/**
 * Backend Enums
 * 백엔드 API 응답값과 동기화되는 enum 정의
 * Style 정보 포함
 *
 * @sync into-seal-server/into-seal-common/src/main/java/kr/intosoft/seal/common/enums
 */

// cat. into common modules
import {hexToRgba} from "@into/into-common-utils";

const BackendEnum = {
    // ========================================
    // Common
    // ========================================

    /**
     * Operator
     * 연산자
     * @see Operator.java
     */
    BE_OPERATOR: {
        EQUAL: "=",
        NOT_EQUAL: "!=",
        CONTAINS: "contains",
        GREATER_THAN: ">",
        LESS_THAN: "<",
        GREATER_OR_EQUAL: ">=",
        LESS_OR_EQUAL: "<=",
        BETWEEN: "between",
        IN: "in",
        NOT_IN: "not_in",
        IS_NULL: "is_null",
        IS_NOT_NULL: "is_not_null",
        EXISTS: "exists",
        NOT_EXISTS: "not_exists",
        AND: "and",
        OR: "or",
    },

    /**
     * HTTP Method
     * HTTP 메서드
     * @see HttpMethod.java
     */
    BE_HTTP_METHOD: {
        GET: "GET",
        POST: "POST",
        PUT: "PUT",
        PATCH: "PATCH",
        DELETE: "DELETE",
    },

    /**
     * Method
     * 메서드
     * @see Method.java
     */
    BE_METHOD: {
        CREATE: "CREATE",
        READ: "READ",
        UPDATE: "UPDATE",
        DELETE: "DELETE",
    },

    /**
     * Priority
     * 우선순위
     * @see Priority.java
     */
    BE_PRIORITY: {
        HIGHEST: "HIGHEST",
        HIGH: "HIGH",
        NORMAL: "NORMAL",
        LOW: "LOW",
        LOWEST: "LOWEST",
    },

    /**
     * Time Unit
     * 시간 단위
     * @see TimeUnit.java
     */
    BE_TIME_UNIT: {
        SECOND: "SECOND",
        MINUTE: "MINUTE",
        HOUR: "HOUR",
    },

    /**
     * History Source
     * 이력 소스
     * @see HistorySource.java
     */
    BE_HISTORY_SOURCE: {
        INTERNAL: "INTERNAL",
        EXTERNAL: "EXTERNAL",
    },

    /**
     * History Action
     * 이력 액션
     * @see HistoryAction.java
     */
    BE_HISTORY_ACTION: {
        CREATE: "CREATE",
        UPDATE: "UPDATE",
        REVERT: "REVERT",
        DELETE: "DELETE",
        RESTORE: "RESTORE",
    },

    // ========================================
    // User
    // ========================================

    /**
     * User Role
     * 사용자 권한
     * @see UserRole.java
     */
    BE_USER_ROLE: {
        SYSTEM_ADMINISTRATOR: "SYSTEM_ADMINISTRATOR",
        USER: "USER",
    },

    BE_USER_ROLE_STYLE: {
        SYSTEM_ADMINISTRATOR: {
            color: "#DC2626",
            background: hexToRgba("#DC2626", 0.1)
        },
        USER: {
            color: "#6B7280",
            background: hexToRgba("#6B7280", 0.1)
        },
    },

    /**
     * User State
     * 사용자 상태
     * @see UserState.java
     */
    BE_USER_STATE: {
        ACTIVE: "ACTIVE",
        INACTIVE: "INACTIVE",
        WITHDRAWAL: "WITHDRAWAL",
    },

    BE_USER_STATE_STYLE: {
        ACTIVE: {
            color: "#10B981",
            background: hexToRgba("#10B981", 0.1)
        },
        INACTIVE: {
            color: "#6B7280",
            background: hexToRgba("#6B7280", 0.1)
        },
        WITHDRAWAL: {
            color: "#374151",
            background: hexToRgba("#374151", 0.1)
        },
    },

    /**
     * User Authority
     * 사용자 권한
     * @see UserAuthority.java
     */
    BE_USER_AUTHORITY: {
        SYSTEM_ADMINISTRATOR_CREATE: "SYSTEM_ADMINISTRATOR_CREATE",
        SYSTEM_ADMINISTRATOR_READ: "SYSTEM_ADMINISTRATOR_READ",
        SYSTEM_ADMINISTRATOR_UPDATE: "SYSTEM_ADMINISTRATOR_UPDATE",
        SYSTEM_ADMINISTRATOR_DELETE: "SYSTEM_ADMINISTRATOR_DELETE",
        ADMINISTRATOR_CREATE: "ADMINISTRATOR_CREATE",
        ADMINISTRATOR_READ: "ADMINISTRATOR_READ",
        ADMINISTRATOR_UPDATE: "ADMINISTRATOR_UPDATE",
        ADMINISTRATOR_DELETE: "ADMINISTRATOR_DELETE",
        USER_CREATE: "USER_CREATE",
        USER_READ: "USER_READ",
        USER_UPDATE: "USER_UPDATE",
        USER_DELETE: "USER_DELETE",
    },

    // ========================================
    // Member
    // ========================================

    /**
     * Member Role
     * 구성원 권한
     * @see MemberRole.java
     */
    BE_MEMBER_ROLE: {
        WORKSPACE_ADMINISTRATOR: "WORKSPACE_ADMINISTRATOR",
        ADMINISTRATOR: "ADMINISTRATOR",
        MEMBER: "MEMBER",
    },

    BE_MEMBER_ROLE_STYLE: {
        WORKSPACE_ADMINISTRATOR: {
            color: "#FFFFFF",
            background: "#177BF5",
        },
        ADMINISTRATOR: {
            color: "#177BF5",
            background: hexToRgba("#177BF5", 0.1)
        },
        MEMBER: {
            color: "#454545",
            background: "#F1F1F1",
        },
    },

    /**
     * Member State
     * 구성원 상태
     * @see MemberState.java
     */
    BE_MEMBER_STATE: {
        ACTIVE: "ACTIVE",
        INACTIVE: "INACTIVE",
        LEAVE: "LEAVE",
        RETIRE: "RETIRE",
        WITHDRAWAL: "WITHDRAWAL",
    },

    BE_MEMBER_STATE_STYLE: {
        ACTIVE: {
            color: "#10B981",
            background: hexToRgba("#10B981", 0.1)
        },
        INACTIVE: {
            color: "#6B7280",
            background: hexToRgba("#6B7280", 0.1)
        },
        LEAVE: {
            color: "#F59E0B",
            background: hexToRgba("#F59E0B", 0.1)
        },
        RETIRE: {
            color: "#EF4444",
            background: hexToRgba("#EF4444", 0.1)
        },
        WITHDRAWAL: {
            color: "#374151",
            background: hexToRgba("#374151", 0.1)
        },
    },

    /**
     * Member Authority
     * 구성원 권한
     * @see MemberAuthority.java
     */
    BE_MEMBER_AUTHORITY: {
        WORKSPACE_ADMINISTRATOR_CREATE: "WORKSPACE_ADMINISTRATOR_CREATE",
        WORKSPACE_ADMINISTRATOR_READ: "WORKSPACE_ADMINISTRATOR_READ",
        WORKSPACE_ADMINISTRATOR_UPDATE: "WORKSPACE_ADMINISTRATOR_UPDATE",
        WORKSPACE_ADMINISTRATOR_DELETE: "WORKSPACE_ADMINISTRATOR_DELETE",
        ADMINISTRATOR_CREATE: "ADMINISTRATOR_CREATE",
        ADMINISTRATOR_READ: "ADMINISTRATOR_READ",
        ADMINISTRATOR_UPDATE: "ADMINISTRATOR_UPDATE",
        ADMINISTRATOR_DELETE: "ADMINISTRATOR_DELETE",
        MEMBER_CREATE: "MEMBER_CREATE",
        MEMBER_READ: "MEMBER_READ",
        MEMBER_UPDATE: "MEMBER_UPDATE",
        MEMBER_DELETE: "MEMBER_DELETE",
    },

    /**
     * Department Member Role
     * 부서 구성원 권한
     * @see DepartmentMemberRole.java
     */
    BE_DEPARTMENT_MEMBER_ROLE: {
        DEPARTMENT_HEAD: "DEPARTMENT_HEAD",
        DEPARTMENT_MEMBER: "DEPARTMENT_MEMBER",
    },

    BE_DEPARTMENT_MEMBER_ROLE_STYLE: {
        DEPARTMENT_HEAD: {
            color: "#177BF5",
            background: hexToRgba("#177BF5", 0.1)
        },
        DEPARTMENT_MEMBER: {
            color: "#454545",
            background: "#F1F1F1",
        },
    },

    // 문서 액션 수행자 유형 (본인/부서장 대리/관리자 대리)
    BE_DOCUMENT_PARTICIPANT_ACTOR_TYPE: {
        NONE: "NONE",
        DEPARTMENT_HEAD: "DEPARTMENT_HEAD",
        ADMIN: "ADMIN",
    },

    BE_DOCUMENT_PARTICIPANT_ACTOR_TYPE_STYLE: {
        NONE: {
            color: "#454545",
            background: "#F1F1F1",
        },
        DEPARTMENT_HEAD: {
            color: "#177BF5",
            background: hexToRgba("#177BF5", 0.1)
        },
        ADMIN: {
            color: "#177BF5",
            background: hexToRgba("#177BF5", 0.1)
        },
    },

    // ========================================
    // Label
    // ========================================

    /**
     * Label Type
     * 라벨 타입
     * @see LabelType.java
     */
    BE_LABEL_TYPE: {
        /** @deprecated */
        NONE: "NONE",
        MEMBER_TYPE: "MEMBER_TYPE",
        MEMBER_POSITION: "MEMBER_POSITION",
        MEMBER_RESPONSIBILITY: "MEMBER_RESPONSIBILITY",
        MEMBER_RANK: "MEMBER_RANK",
        DOCUMENT_TYPE: "DOCUMENT_TYPE",
        APPROVAL_REQUEST_REASON: "APPROVAL_REQUEST_REASON",
        SEAL_TYPE: "SEAL_TYPE",
    },

    /**
     * Label Reference Type
     * 라벨 참조 타입
     * @see LabelReferenceType.java
     */
    BE_LABEL_REFERENCE_TYPE: {
        MEMBER: "MEMBER",
        SEAL: "SEAL",
        DOCUMENT_TEMPLATE: "DOCUMENT_TEMPLATE",
        DOCUMENT_DRAFT: "DOCUMENT_DRAFT",
    },

    // ========================================
    // Seal
    // ========================================

    /**
     * Seal Type
     * 인장 타입
     * @see SealType.java
     */
    BE_SEAL_TYPE: {
        NONE: "NONE",
        CORPORATE: "CORPORATE",
    },

    BE_SEAL_TYPE_STYLE: {
        // Figma 5568-1854: 아웃라인(테두리 lines_2) + 유형별 텍스트 컬러
        NONE: {
            color: "#00B4B4",
            background: "#FFFFFF",
            border: "1px solid #EBEDF2"
        },
        CORPORATE: {
            color: "#FF6F00",
            background: "#FFFFFF",
            border: "1px solid #EBEDF2"
        },
    },

    /**
     * Seal State
     * 인장 상태
     * @see SealState.java
     */
    BE_SEAL_STATE: {
        ACTIVE: "ACTIVE",
        INACTIVE: "INACTIVE",
    },

    BE_SEAL_STATE_STYLE: {
        ACTIVE: {
            color: "#10B981",
            background: hexToRgba("#10B981", 0.1)
        },
        INACTIVE: {
            color: "#6B7280",
            background: hexToRgba("#6B7280", 0.1)
        },
    },

    /**
     * Seal Status
     * 인장 상태
     * @see SealStatus.java
     */
    BE_SEAL_STATUS: {
        NONE: "NONE",
        DELETE: "DELETE",
    },

    /**
     * Seal Ledger Status
     * 인장대장 상태
     * @see SealLedgerStatus.java
     */
    BE_SEAL_LEDGER_STATUS: {
        NONE: "NONE",
        PROGRESS: "PROGRESS",
        COMPLETE: "COMPLETE",
        REJECT: "REJECT",
        DELETE: "DELETE",
    },

    BE_SEAL_LEDGER_STATUS_STYLE: {
        NONE: {
            color: "#6B7280",
            background: hexToRgba("#6B7280", 0.1)
        },
        PROGRESS: {
            color: "#3B82F6",
            background: hexToRgba("#3B82F6", 0.1)
        },
        COMPLETE: {
            color: "#10B981",
            background: hexToRgba("#10B981", 0.1)
        },
        REJECT: {
            color: "#EF4444",
            background: hexToRgba("#EF4444", 0.1)
        },
        DELETE: {
            color: "#374151",
            background: hexToRgba("#374151", 0.1)
        },
    },

    /**
     * Seal Ledger Convert Status
     * 인장대장 변환 상태
     * @see SealLedgerConvertStatus.java
     */
    BE_SEAL_LEDGER_CONVERT_STATUS: {
        WAIT: "WAIT",
        PROGRESS: "PROGRESS",
        FAILURE: "FAILURE",
        COMPLETE: "COMPLETE",
    },

    /**
     * Seal Ledger Reference Type
     * 인장대장 수록 기준 — DOCUMENT 는 레거시, 현재는 인스턴스 기준
     * @see SealLedgerReferenceType.java
     */
    BE_SEAL_LEDGER_REFERENCE_TYPE: {
        DOCUMENT: "DOCUMENT",
        DOCUMENT_INSTANCE: "DOCUMENT_INSTANCE",
    },

    BE_SEAL_LEDGER_CONVERT_STATUS_STYLE: {
        WAIT: {
            color: "#F59E0B",
            background: hexToRgba("#F59E0B", 0.1)
        },
        PROGRESS: {
            color: "#3B82F6",
            background: hexToRgba("#3B82F6", 0.1)
        },
        FAILURE: {
            color: "#EF4444",
            background: hexToRgba("#EF4444", 0.1)
        },
        COMPLETE: {
            color: "#10B981",
            background: hexToRgba("#10B981", 0.1)
        },
    },

    // ========================================
    // Document Template
    // ========================================

    /**
     * Document Template State
     * 문서 템플릿 상태
     * @see DocumentTemplateState.java
     */
    BE_DOCUMENT_TEMPLATE_STATE: {
        ACTIVE: "ACTIVE",
        INACTIVE: "INACTIVE",
    },

    BE_DOCUMENT_TEMPLATE_STATE_STYLE: {
        ACTIVE: {
            color: "#10B981",
            background: hexToRgba("#10B981", 0.1)
        },
        INACTIVE: {
            color: "#6B7280",
            background: hexToRgba("#6B7280", 0.1)
        },
    },

    /**
     * Document Template Status
     * 문서 템플릿 상태
     * @see DocumentTemplateStatus.java
     */
    BE_DOCUMENT_TEMPLATE_STATUS: {
        NONE: "NONE",
        DELETE: "DELETE",
        HARD_DELETE: "HARD_DELETE",
    },

    BE_DOCUMENT_TEMPLATE_STATUS_STYLE: {
        NONE: {
            color: "#10B981",
            background: hexToRgba("#10B981", 0.1)
        },
        DELETE: {
            color: "#EF4444",
            background: hexToRgba("#EF4444", 0.1)
        },
        HARD_DELETE: {
            color: "#374151",
            background: hexToRgba("#374151", 0.1)
        },
    },

    /**
     * Document Template Favorite Reference Type
     * 문서 템플릿 즐겨찾기 참조 타입
     * @see DocumentTemplateFavoriteReferenceType.java
     */
    BE_DOCUMENT_TEMPLATE_FAVORITE_REFERENCE_TYPE: {
        MEMBER: "MEMBER",
    },

    // ========================================
    // Document Draft
    // ========================================

    /**
     * Document Draft Status
     * 문서 임시저장 상태
     * @see DocumentDraftStatus.java
     */
    BE_DOCUMENT_DRAFT_STATUS: {
        NONE: "NONE",
        DELETE: "DELETE",
        HARD_DELETE: "HARD_DELETE",
    },

    BE_DOCUMENT_DRAFT_STATUS_STYLE: {
        NONE: {
            color: "#10B981",
            background: hexToRgba("#10B981", 0.1)
        },
        DELETE: {
            color: "#EF4444",
            background: hexToRgba("#EF4444", 0.1)
        },
        HARD_DELETE: {
            color: "#374151",
            background: hexToRgba("#374151", 0.1)
        },
    },

    /**
     * Base Document Type
     * 기본 문서 타입
     * @see BaseDocumentType.java
     */
    BE_BASE_DOCUMENT_TYPE: {
        DOCUMENT_DRAFT: "DOCUMENT_DRAFT",
        DOCUMENT_TEMPLATE: "DOCUMENT_TEMPLATE",
    },

    // ========================================
    // Document
    // ========================================

    /**
     * Document Status
     * 문서 상태
     * @see DocumentStatus.java
     */
    BE_DOCUMENT_STATUS: {
        WAIT: "WAIT",
        PROGRESS: "PROGRESS",
        COMPLETE: "COMPLETE",
        REJECT: "REJECT",
        CANCEL: "CANCEL",
        /** @deprecated */
        EXPIRE: "EXPIRE",
        DELETE: "DELETE",
    },

    BE_DOCUMENT_STATUS_STYLE: {
        WAIT: {
            color: "#959BA6",
            background: hexToRgba("#959BA6", 0.1)
        },
        PROGRESS: {
            color: "#177BF5",
            background: hexToRgba("#177BF5", 0.1)
        },
        COMPLETE: {
            color: "#34B977",
            background: hexToRgba("#34B977", 0.1)
        },
        REJECT: {
            color: "#DD2025",
            background: hexToRgba("#DD2025", 0.1)
        },
        CANCEL: {
            color: "#F42F9A",
            background: hexToRgba("#F42F9A", 0.1)
        },
        EXPIRE: {
            color: "#8B5CF6",
            background: hexToRgba("#8B5CF6", 0.1)
        },
        DELETE: {
            color: "#374151",
            background: hexToRgba("#374151", 0.1)
        },
    },

    /**
     * Document Convert Status
     * 문서 변환 상태
     * @see DocumentConvertStatus.java
     */
    BE_DOCUMENT_CONVERT_STATUS: {
        NONE: "NONE",
        WAIT: "WAIT",
        READY: "READY",
        PROGRESS: "PROGRESS",
        FAILURE: "FAILURE",
        COMPLETE: "COMPLETE",
    },

    BE_DOCUMENT_CONVERT_STATUS_STYLE: {
        NONE: {
            color: "#6B7280",
            background: hexToRgba("#6B7280", 0.1)
        },
        WAIT: {
            color: "#F59E0B",
            background: hexToRgba("#F59E0B", 0.1)
        },
        READY: {
            color: "#F59E0B",
            background: hexToRgba("#F59E0B", 0.1)
        },
        PROGRESS: {
            color: "#3B82F6",
            background: hexToRgba("#3B82F6", 0.1)
        },
        FAILURE: {
            color: "#EF4444",
            background: hexToRgba("#EF4444", 0.1)
        },
        COMPLETE: {
            color: "#10B981",
            background: hexToRgba("#10B981", 0.1)
        },
    },

    /**
     * Document Seal History Action
     * 문서 인장 이력 액션
     * @see DocumentSealHistoryAction.java
     */
    BE_DOCUMENT_SEAL_HISTORY_ACTION: {
        CREATE: "CREATE",
        DELETE: "DELETE",
    },

    /**
     * Document Label Reference Type
     * 문서 라벨 참조 타입
     * @see DocumentLabelReferenceType.java
     */
    BE_DOCUMENT_LABEL_REFERENCE_TYPE: {
        DOCUMENT: "DOCUMENT",
        DOCUMENT_PARTICIPANT: "DOCUMENT_PARTICIPANT",
        DOCUMENT_SEAL: "DOCUMENT_SEAL",
    },

    /**
     * Document Data Type
     * 문서 데이터 타입
     * @see DocumentDataType.java
     */
    BE_DOCUMENT_DATA_TYPE: {
        INTERNAL: "INTERNAL",
        EXTERNAL: "EXTERNAL",
    },

    /**
     * Document Download Domain
     * 문서 다운로드 도메인
     * @see DocumentDownloadDomain.java
     */
    BE_DOCUMENT_DOWNLOAD_DOMAIN: {
        OUTPUT: "OUTPUT",
        TRAIL: "TRAIL",
        OUTPUT_DISTRIBUTION: "OUTPUT_DISTRIBUTION",
        CERTIFICATE_SEAL: "CERTIFICATE_SEAL",
        ATTACHMENT: "ATTACHMENT",
    },

    /**
     * Document Process History Type
     * 문서 처리 이력 타입
     * @see DocumentProcessHistoryType.java
     */
    BE_DOCUMENT_PROCESS_HISTORY_TYPE: {
        DOCUMENT_CREATE: "DOCUMENT_CREATE",
        DOCUMENT_PROGRESS: "DOCUMENT_PROGRESS",
        DOCUMENT_APPROVER_CREATE: "DOCUMENT_APPROVER_CREATE",
        DOCUMENT_APPROVER_COMPLETE: "DOCUMENT_APPROVER_COMPLETE",
        DOCUMENT_APPROVER_REJECT: "DOCUMENT_APPROVER_REJECT",
        DOCUMENT_SEAL_APPROVER_CREATE: "DOCUMENT_SEAL_APPROVER_CREATE",
        DOCUMENT_SEAL_APPROVER_COMPLETE: "DOCUMENT_SEAL_APPROVER_COMPLETE",
        DOCUMENT_SEAL_APPROVER_REJECT: "DOCUMENT_SEAL_APPROVER_REJECT",
        DOCUMENT_SEAL_MANAGER_CREATE: "DOCUMENT_SEAL_MANAGER_CREATE",
        DOCUMENT_SEAL_MANAGER_COMPLETE: "DOCUMENT_SEAL_MANAGER_COMPLETE",
        DOCUMENT_SEAL_MANAGER_REJECT: "DOCUMENT_SEAL_MANAGER_REJECT",
        DOCUMENT_COMPLETE: "DOCUMENT_COMPLETE",
        DOCUMENT_REJECT: "DOCUMENT_REJECT",
        DOCUMENT_CANCEL: "DOCUMENT_CANCEL",
    },

    /**
     * Document Process History Reference Type
     * 문서 처리 이력 참조 타입
     * @see DocumentProcessHistoryReferenceType.java
     */
    BE_DOCUMENT_PROCESS_HISTORY_REFERENCE_TYPE: {
        DOCUMENT: "DOCUMENT",
        DOCUMENT_APPROVER: "DOCUMENT_APPROVER",
        DOCUMENT_SEAL_APPROVER: "DOCUMENT_SEAL_APPROVER",
        DOCUMENT_SEAL_MANAGER: "DOCUMENT_SEAL_MANAGER",
    },

    /**
     * Document Resource Convert Status
     * 문서 리소스 변환 상태
     * @see DocumentResourceConvertStatus.java
     */
    BE_DOCUMENT_RESOURCE_CONVERT_STATUS: {
        FAILURE: "FAILURE",
        COMPLETE: "COMPLETE",
    },

    BE_DOCUMENT_RESOURCE_CONVERT_STATUS_STYLE: {
        FAILURE: {
            color: "#EF4444",
            background: hexToRgba("#EF4444", 0.1)
        },
        COMPLETE: {
            color: "#10B981",
            background: hexToRgba("#10B981", 0.1)
        },
    },

    // ========================================
    // Document Requester
    // ========================================

    /**
     * Document Requester Status
     * 문서 요청자 상태
     * (서버에서 별도 enum 없음, 클라이언트 정의)
     */
    BE_DOCUMENT_REQUESTER_STATUS: {
        NONE: "NONE",
        CANCEL: "CANCEL",
    },

    // ========================================
    // Document Approver
    // ========================================

    /**
     * Document Approver Status
     * 결재자 상태
     * @see DocumentApproverStatus.java
     */
    BE_DOCUMENT_APPROVER_STATUS: {
        NONE: "NONE",
        WAIT: "WAIT",
        READY: "READY",
        COMPLETE: "COMPLETE",
        REJECT: "REJECT",
    },

    BE_DOCUMENT_APPROVER_STATUS_STYLE: {
        NONE: {
            color: "#959BA6",
            background: '#F1F1F1'
        },
        WAIT: {
            color: "#959BA6",
            background: '#F1F1F1'
        },
        READY: {
            color: "#177BF5",
            background: hexToRgba("#177BF5", 0.1)
        },
        COMPLETE: {
            color: "#34B977",
            background: hexToRgba("#34B977", 0.1)
        },
        REJECT: {
            color: "#DD2025",
            background: hexToRgba("#DD2025", 0.1)
        },
    },

    // ========================================
    // Document Seal Approver (날인 결재자)
    // ========================================

    /**
     * Document Seal Approver Status
     * 날인 결재자 상태
     * @see DocumentSealApproverStatus.java
     */
    BE_DOCUMENT_SEAL_APPROVER_STATUS: {
        NONE: "NONE",
        WAIT: "WAIT",
        READY: "READY",
        COMPLETE: "COMPLETE",
        REJECT: "REJECT",
    },

    BE_DOCUMENT_SEAL_APPROVER_STATUS_STYLE: {
        NONE: {
            color: "#6B7280",
            background: hexToRgba("#6B7280", 0.1)
        },
        WAIT: {
            color: "#F59E0B",
            background: hexToRgba("#F59E0B", 0.1)
        },
        READY: {
            color: "#3B82F6",
            background: hexToRgba("#3B82F6", 0.1)
        },
        COMPLETE: {
            color: "#10B981",
            background: hexToRgba("#10B981", 0.1)
        },
        REJECT: {
            color: "#EF4444",
            background: hexToRgba("#EF4444", 0.1)
        },
    },

    // ========================================
    // Document Seal Manager (날인자)
    // ========================================

    /**
     * Document Seal Manager Status
     * 날인자 상태
     * @see DocumentSealManagerStatus.java
     */
    BE_DOCUMENT_SEAL_MANAGER_STATUS: {
        NONE: "NONE",
        WAIT: "WAIT",
        READY: "READY",
        COMPLETE: "COMPLETE",
        REJECT: "REJECT",
    },

    BE_DOCUMENT_SEAL_MANAGER_STATUS_STYLE: {
        NONE: {
            color: "#6B7280",
            background: hexToRgba("#6B7280", 0.1)
        },
        WAIT: {
            color: "#F59E0B",
            background: hexToRgba("#F59E0B", 0.1)
        },
        READY: {
            color: "#3B82F6",
            background: hexToRgba("#3B82F6", 0.1)
        },
        COMPLETE: {
            color: "#10B981",
            background: hexToRgba("#10B981", 0.1)
        },
        REJECT: {
            color: "#EF4444",
            background: hexToRgba("#EF4444", 0.1)
        },
    },

    // ========================================
    // Document Revoker
    // ========================================

    /**
     * Document Revoker Type
     * 문서 취소자 타입
     * @see DocumentRevokerType.java
     */
    BE_DOCUMENT_REVOKER_TYPE: {
        MEMBER: "MEMBER",
        DOCUMENT_REQUESTER: "DOCUMENT_REQUESTER",
        DOCUMENT_APPROVER: "DOCUMENT_APPROVER",
        DOCUMENT_SEAL_APPROVER: "DOCUMENT_SEAL_APPROVER",
        DOCUMENT_SEAL_MANAGER: "DOCUMENT_SEAL_MANAGER",
    },

    /**
     * Document Revoker Reference Type
     * 문서 취소자 참조 타입
     * @see DocumentRevokerReferenceType.java
     */
    BE_DOCUMENT_REVOKER_REFERENCE_TYPE: {
        MEMBER: "MEMBER",
        DOCUMENT_REQUESTER: "DOCUMENT_REQUESTER",
        DOCUMENT_APPROVER: "DOCUMENT_APPROVER",
        DOCUMENT_SEAL_MANAGER: "DOCUMENT_SEAL_MANAGER",
    },

    // ========================================
    // Notification
    // ========================================

    /**
     * Notification Type
     * 알림 타입
     * @see NotificationType.java
     */
    BE_NOTIFICATION_TYPE: {
        EMAIL: "EMAIL",
        MOBILE: "MOBILE",
    },

    /**
     * Notification Context
     * 알림 컨텍스트
     * @see NotificationContext.java
     */
    BE_NOTIFICATION_CONTEXT: {
        DOCUMENT_PROGRESS: "DOCUMENT_PROGRESS",
        DOCUMENT_COMPLETE: "DOCUMENT_COMPLETE",
        DOCUMENT_REJECT: "DOCUMENT_REJECT",
        DOCUMENT_CANCEL: "DOCUMENT_CANCEL",
        DOCUMENT_APPROVER_READY: "DOCUMENT_APPROVER_READY",
        DOCUMENT_SEAL_APPROVER_READY: "DOCUMENT_SEAL_APPROVER_READY",
        DOCUMENT_SEAL_MANAGER_READY: "DOCUMENT_SEAL_MANAGER_READY",
    },

    /**
     * Notification Status
     * 알림 상태
     * @see NotificationStatus.java
     */
    BE_NOTIFICATION_STATUS: {
        WAIT: "WAIT",
        PROGRESS: "PROGRESS",
        FAILURE: "FAILURE",
        COMPLETE: "COMPLETE",
    },

    BE_NOTIFICATION_STATUS_STYLE: {
        WAIT: {
            color: "#F59E0B",
            background: hexToRgba("#F59E0B", 0.1)
        },
        PROGRESS: {
            color: "#3B82F6",
            background: hexToRgba("#3B82F6", 0.1)
        },
        FAILURE: {
            color: "#EF4444",
            background: hexToRgba("#EF4444", 0.1)
        },
        COMPLETE: {
            color: "#10B981",
            background: hexToRgba("#10B981", 0.1)
        },
    },

    /**
     * Notification Reference Type
     * 알림 참조 타입
     * @see NotificationReferenceType.java
     */
    BE_NOTIFICATION_REFERENCE_TYPE: {
        DOCUMENT: "DOCUMENT",
    },

    /**
     * Notification Receiver Reference Type
     * 알림 수신자 참조 타입
     * @see NotificationReceiverReferenceType.java
     */
    BE_NOTIFICATION_RECEIVER_REFERENCE_TYPE: {
        MEMBER: "MEMBER",
        DOCUMENT_REQUESTER: "DOCUMENT_REQUESTER",
        DOCUMENT_APPROVER: "DOCUMENT_APPROVER",
        DOCUMENT_SEAL_MANAGER: "DOCUMENT_SEAL_MANAGER",
        DOCUMENT_SEAL_APPROVER: "DOCUMENT_SEAL_APPROVER",
    },

    // ========================================
    // Resource
    // ========================================

    /**
     * Resource Type
     * 리소스 타입
     * @see ResourceType.java
     */
    BE_RESOURCE_TYPE: {
        BLOB: "BLOB",
        PATH: "PATH",
        S3: "S3",
    },

    /**
     * Resource Reference Type
     * 리소스 참조 타입
     * @see ResourceReferenceType.java
     */
    BE_RESOURCE_REFERENCE_TYPE: {
        MEMBER: "MEMBER",
        DOCUMENT_TEMPLATE: "DOCUMENT_TEMPLATE",
        DOCUMENT_DRAFT: "DOCUMENT_DRAFT",
        DOCUMENT: "DOCUMENT",
        DOCUMENT_REDACTED: "DOCUMENT_REDACTED",
        DOCUMENT_OUTPUT: "DOCUMENT_OUTPUT",
        DOCUMENT_OUTPUT_DISTRIBUTION: "DOCUMENT_OUTPUT_DISTRIBUTION",
        DOCUMENT_TRAIL: "DOCUMENT_TRAIL",
        DOCUMENT_CERTIFICATE_SEAL: "DOCUMENT_CERTIFICATE_SEAL",
        DOCUMENT_ATTACHMENT: "DOCUMENT_ATTACHMENT",
        DOCUMENT_REQUESTER: "DOCUMENT_REQUESTER",
        DOCUMENT_APPROVER: "DOCUMENT_APPROVER",
        DOCUMENT_SEAL_APPROVER: "DOCUMENT_SEAL_APPROVER",
        DOCUMENT_SEAL_MANAGER: "DOCUMENT_SEAL_MANAGER",
        DOCUMENT_REVOKER: "DOCUMENT_REVOKER",
        SEAL_LEDGER: "SEAL_LEDGER",
        DOCUMENT_DRAFT_ATTACHMENT: "DOCUMENT_DRAFT_ATTACHMENT",
        DOCUMENT_INSTANCE_OUTPUT: "DOCUMENT_INSTANCE_OUTPUT",
        DOCUMENT_INSTANCE_OUTPUT_DISTRIBUTION: "DOCUMENT_INSTANCE_OUTPUT_DISTRIBUTION",
        DOCUMENT_INSTANCE_TRAIL: "DOCUMENT_INSTANCE_TRAIL",
        DOCUMENT_INSTANCE_CERTIFICATE_SEAL: "DOCUMENT_INSTANCE_CERTIFICATE_SEAL",
    },

    // ========================================
    // Attachment
    // ========================================

    /**
     * Attachment Reference Type
     * 첨부파일 참조 타입
     * @see AttachmentReferenceType.java
     */
    BE_ATTACHMENT_REFERENCE_TYPE: {
        DOCUMENT: "DOCUMENT",
    },

    // ========================================
    // Role
    // ========================================

    /**
     * Role Type
     * 역할 타입
     * @see RoleType.java
     */
    BE_ROLE_TYPE: {
        USER: "USER",
        MEMBER: "MEMBER",
        SEAL_APPROVER: "SEAL_APPROVER",
    },

    // ========================================
    // Callback
    // ========================================

    /**
     * Callback Reference Type
     * 콜백 참조 타입
     * @see CallbackReferenceType.java
     */
    BE_CALLBACK_REFERENCE_TYPE: {
        DOCUMENT: "DOCUMENT",
    },

    // ========================================
    // Hook
    // ========================================

    /**
     * Hook Event Domain
     * 훅 이벤트 도메인
     * @see HookEventDomain.java
     */
    BE_HOOK_EVENT_DOMAIN: {
        DOCUMENT: "DOCUMENT",
        DOCUMENT_APPROVER: "DOCUMENT_APPROVER",
        DOCUMENT_SEAL_APPROVER: "DOCUMENT_SEAL_APPROVER",
        DOCUMENT_SEAL_MANAGER: "DOCUMENT_SEAL_MANAGER",
        SEAL_LEDGER: "SEAL_LEDGER",
    },

    /**
     * Hook Event Type
     * 훅 이벤트 타입
     * @see HookEventType.java
     */
    BE_HOOK_EVENT_TYPE: {
        STATUS_PROGRESS: "STATUS_PROGRESS",
        STATUS_COMPLETE: "STATUS_COMPLETE",
        STATUS_REJECT: "STATUS_REJECT",
        STATUS_CANCEL: "STATUS_CANCEL",
        STATUS_DELETE: "STATUS_DELETE",
        STATUS_CONVERT_FAILURE: "STATUS_CONVERT_FAILURE",
    },

    /**
     * Hook Event Reference Type
     * 훅 이벤트 참조 타입
     * @see HookEventReferenceType.java
     */
    BE_HOOK_EVENT_REFERENCE_TYPE: {
        WEBHOOK: "WEBHOOK",
        CALLBACK: "CALLBACK",
    },

    /**
     * Hook Reference Type
     * 훅 참조 타입
     * @see HookReferenceType.java
     */
    BE_HOOK_REFERENCE_TYPE: {
        WEBHOOK: "WEBHOOK",
        CALLBACK: "CALLBACK",
    },

    /**
     * Hook Status
     * 훅 상태
     * @see HookStatus.java
     */
    BE_HOOK_STATUS: {
        WAIT: "WAIT",
        PROGRESS: "PROGRESS",
        FAILURE: "FAILURE",
        COMPLETE: "COMPLETE",
    },

    BE_HOOK_STATUS_STYLE: {
        WAIT: {
            color: "#F59E0B",
            background: hexToRgba("#F59E0B", 0.1)
        },
        PROGRESS: {
            color: "#3B82F6",
            background: hexToRgba("#3B82F6", 0.1)
        },
        FAILURE: {
            color: "#EF4444",
            background: hexToRgba("#EF4444", 0.1)
        },
        COMPLETE: {
            color: "#10B981",
            background: hexToRgba("#10B981", 0.1)
        },
    },

    /**
     * Webhook History Result
     * 웹훅 전송 이력 결과 (서버 BFF 파생값: 훅 상태 COMPLETE→SUCCESS / FAILURE→FAIL / WAIT·PROGRESS→RETRY)
     * @see WebhookHistorySagaAdminInternalWebService#toResult
     */
    BE_WEBHOOK_HISTORY_RESULT: {
        SUCCESS: "SUCCESS",
        FAIL: "FAIL",
        RETRY: "RETRY",
    },
    BE_WEBHOOK_HISTORY_RESULT_STYLE: {
        SUCCESS: {
            color: "#1E8E3E",
            background: "#E9F7EF",
        },
        FAIL: {
            color: "#D93025",
            background: "#FDECEC",
        },
        RETRY: {
            color: "#C77700",
            background: "#FBF1E6",
        },
    },

    // ========================================
    // Workspace
    // ========================================

    /**
     * Workspace Config Key
     * 워크스페이스 설정 키
     * @see WorkspaceConfigKey.java
     */
    BE_WORKSPACE_CONFIG_KEY: {
        ORGANIZATION_SYNC_ENABLED: "ORGANIZATION_SYNC_ENABLED",
        ORGANIZATION_SYNC_DATASOURCE_TYPE: "ORGANIZATION_SYNC_DATASOURCE_TYPE",
        ORGANIZATION_SYNC_API_URL: "ORGANIZATION_SYNC_API_URL",
    },

    // ========================================
    // Owner
    // ========================================

    // ========================================
    // Document Extract
    // ========================================

    /**
     * Document Extract Data Domain
     * 문서 추출 데이터 도메인
     * @see DocumentExtractDataDomain.java
     */
    BE_DOCUMENT_EXTRACT_DATA_DOMAIN: {
        DOCUMENT: "DOCUMENT",
        SIGNER: "SIGNER",
    },

    // ========================================
    // History
    // ========================================

    /**
     * History Reference Type
     * 이력 참조 타입
     * @see HistoryReferenceType.java
     */
    BE_HISTORY_REFERENCE_TYPE: {
        SEAL: "SEAL",
        DOCUMENT_TEMPLATE: "DOCUMENT_TEMPLATE",
    },

    // ========================================
    // Published Department
    // ========================================

    // ========================================
    // Token
    // ========================================

    /**
     * Acted Role
     * 삭제 참여자 수행 권한
     * @see ActedRole.java
     */
    BE_ACTED_ROLE: {
        NONE: "NONE",
        DEPT_HEAD: "DEPT_HEAD",
        ADMINISTRATOR: "ADMINISTRATOR",
    },
} as const;

export default BackendEnum;
